<template>
    <div class="station-monitor">
        <div class="sidebar__section sidebar__section--border-bottom sidebar__section--small-vertical-padding ui-spread-center-vertically copy-light-small">
            <div>{{ todayFormatted }}</div>
            <div>
                <div class="station-monitor__sorting">
                    <div class="station-monitor__sorting__label bold-small-label">
                        {{ activeSortingShortLabel }}
                    </div>
                    <SmrSelect 
                        class="station-monitor__sorting__select" 
                        :value="currentSorting" 
                        :options="sortingOptions" 
                        :multiple="false" 
                        :clearable="false" 
                        :tracking-data="trackingData"
                        :additionalPopperClass="'station-monitor__sorting__dropdown'" 
                        @change="sortingChangeHandler" 
                        @visibleChange="sortingVisibleChangeHandler" />
                </div>
            </div>
        </div>

        <section class="sidebar__section station-monitor__modes" v-if="availableModes">
            <ItemSlider slider-class="station-monitor__modes__slider" :slider-options="sliderOptions" ref="nav-slider">
                <SwiperSlide 
                    class="station-monitor__modes__item" 
                    v-for="mode in availableModes" 
                    :key="mode.key"
                >
                    <SmrButton size="tiny" :color="activeMode === mode.key ? 'yellow' : 'grey'" class="wide station-monitor__modes__button" :class="{'is-active': activeMode === mode.key}" :round="true" @click.native="switchActiveMode(mode.key)">
                        {{mode.label}}
                    </SmrButton>
                </SwiperSlide>
            </ItemSlider>
        </section>

        <ContentLoader class="sidebar__section" v-if="loading"/>

        <section class="sidebar__section station-monitor__content" v-if="!loading">
            <div class="station-monitor__result ui-flex" v-for="(result, resultIndex) in results" :key="resultIndex">
                <div class="station-monitor__times">
                    <div v-if="result.timestamp.plannedTime" :class="{ 'cancelled' : result.cancellation && result.cancellation.isCancelled }">{{ result.timestamp.plannedTime | timestampToTime }}</div>
                    <div v-if="result.timestamp.realTime" :class="[(result.timestamp.realTime === result.timestamp.plannedTime ? 'green' : 'orange'), { 'cancelled' : result.cancellation && result.cancellation.isCancelled }]">{{ result.timestamp.realTime | timestampToTime }}</div>
                </div>
                <div class="station-monitor__infos">
                    <div class="station-monitor__infos__main ui-center-vertically">
                        <icon class="station-monitor__infos__icon" :size="20" v-if="result.line.type">
                            <PublicTransportIcon :mode="result.line.type"/>
                        </icon>
                        <span v-if="result.line.iconText" class="station-monitor__infos__tag ui-nowrap tag tag--small tag--dark" :style="{'background-color': result.line.color}">
                            {{ result.line.iconText }}
                        </span>
                        <span v-if="result.line.origin">{{ result.line.origin }}</span>
                        <span v-else>{{ result.line.destination }}</span>
                    </div>
                    <el-collapse v-if="result.operationMessages && result.operationMessages.messages.length" class="station-monitor__cancellation">
                        <el-collapse-item v-for="(operationMessage, messageIndex) in result.operationMessages.messages" :key="messageIndex">
                            <div class="station-monitor__cancellation__title red ui-center-vertically" slot="title">
                                {{ operationMessage.title }}
                                <icon class="station-monitor__cancellation__title__icon" :size="20">
                                    <IconChevronDown />
                                </icon>
                            </div>
                            <div class="station-monitor__cancellation__content copy-light" v-html="operationMessage.text"></div>
                        </el-collapse-item>
                    </el-collapse>
                    <div v-else-if="result.cancellation && result.cancellation.isCancelled">
                        <div class="station-monitor__cancellation__title red ui-center-vertically">
                            Verbindung entfällt
                        </div>
                    </div>
                    <div v-else class="station-monitor__infos__secondary ui-overflow-hidden copy-light">
                        <span class="dot-divider dot-divider--info" v-if="result.remainingTime">In {{ result.remainingTime }}</span>
                        <span class="dot-divider dot-divider--info" :class="{'red': result.platform && result.plannedPlatform !== result.platform}" v-if="result.platform || result.plannedPlatform">Plattform {{result.platform || result.plannedPlatform}}</span>
                    </div>

                </div>
                <div class="station-monitor__icons">
                    <icon class="station-monitor__icons__item" :size="20" v-if="result.line.lowFloorVehicle">
                        <IconWheelchair />
                    </icon>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatTimeFromTimestamp, formatDuration, formatDate, getDayName } from '@/utils/format'
import { PUBLIC_MEANS_OF_TRANSPORT_FULL } from '@data/publicMeansOfTransport';

import Icon from '@/components/Icon.vue'
import ItemSlider from '@/components/ItemSlider.vue'
import SmrButton from '@/components/SmrButton.vue'
import SmrSelect from '@/components/form/SmrSelect.vue'
import PublicTransportIcon from '@/components/PublicTransportIcon.vue'
import ContentLoader from '@/components/ContentLoader.vue'
import IconWheelchair from '@/components/icons/IconWheelchair.vue'
import IconChevronDown from '@/components/icons/IconChevronDown.vue'
import * as api from '@/api';

export default {
    components: {
        Icon,
        ItemSlider,
        SmrButton,
        SmrSelect,
        PublicTransportIcon,
        ContentLoader,
        IconWheelchair,
        IconChevronDown,
    },
    props: {
        stationId: {
            type: [String, Boolean],
            default: false
        },
        lines: {
            type: [Array, Boolean],
            default: false
        },
        activePoi: {
            type: [Boolean, Object],
            default: false,
        },
    },
    data() {
        return {
            sliderOptions: {
                slidesPerView: 'auto',
                spaceBetween: 6,
                slideToClickedSlide: true,
                centeredSlides: true,
                centeredSlidesBounds: true,
                watchOverflow: true,
                touchStartPreventDefault: false,
                freeMode: true,
                freeModeMomentumRatio: 0.5
            },
            activeMode: 'all',
            availableModes: false,
            results: [],
            loading: false,
            calcTimeout: false,
            tabs: [
                {
                    value: ['EXPRESS_BUS', 'REGIONAL_BUS', 'CITY_BUS'],
                    label: 'Bus',
                    key: 'bus',
                    available: false,
                    count: 0,
                },
                {
                    value: ['TRAIN', 'URBAN_RAILWAY', 'CITY_RAILWAY'],
                    label: 'Zug',
                    key: 'train',
                    available: false,
                    count: 0,
                },
                {
                    value: ['TRAM'],
                    label: 'Straßenbahn',
                    key: 'tram',
                    available: false,
                    count: 0,
                },
                {
                    value: ['UNDERGROUND_RAILWAY'],
                    label: 'U-Bahn',
                    key: 'underground',
                    available: false,
                    count: 0,
                }
            ],
            currentSorting: 'DEPARTURE',
            sortingOptions: [
                {
                    value: 'DEPARTURE',
                    label: 'Nach Abfahrt',
                    labelShort: 'Abfahrt',
                },
                {
                    value: 'ARRIVAL',
                    label: 'Nach Ankunft',
                    labelShort: 'Ankunft',
                },
            ],
            trackingData: {
                category: 'Stations',
                action: 'Order',
                name: 'station',            }
        }
    },
    computed: {
        ...mapGetters(['mobile']),
        todayFormatted() {
            const today = new Date()
            return `${getDayName(today)}, ${formatDate(today)}`
        },
        activeSortingShortLabel() {
            const activeSortingOption = this.sortingOptions.find(option => option.value === this.currentSorting)
            return activeSortingOption.labelShort
        }
    },
    filters: {
        timestampToTime(val) {
            return formatTimeFromTimestamp(val);
        }
    },
    created() {
        this.setAvailableModes();
        // avoid double loading when we already have the stations (detail on page load)
        if(this.activePoi.departures) {
            this.results = this.activePoi.departures
        } else {
            this.getResults(); 
        }
    },
    destroyed() {
        this.clearCalcTimeout();
    },
    watch: {
        stationId() {
            this.availableModes = false;
            this.currentSorting = 'DEPARTURE';
            this.setAvailableModes();
            this.getResults();
        },
    },
    methods: {
        sortingChangeHandler(sorting) {
            this.currentSorting = sorting
            this.getResults()
        },
        sortingVisibleChangeHandler(data) {
            if(this.mobile) {
                if(data) {
                    this.$store.commit('setDim', true)
                } else {
                    this.$store.commit('setDim', false)
                }
            }
        },
        switchActiveMode(mode) {
            if(this.activeMode === mode) return false;
            this.activeMode = mode;
            this.getResults();
        },
        async getResults() {
            this.clearCalcTimeout();
            this.loading = true;
            let params = {
                externalId: this.stationId,
                type: this.currentSorting,
            }
            if(this.activeMode !== 'all') {
                const activeTabData = this.tabs.find(tab => tab.key === this.activeMode)
                params.meansOfTransport = activeTabData.value;
            }
            const response = await api.stationMonitor.getMonitor(params);
            if(response.data) {
                if(response.data.arrivals) {
                    this.results = response.data.arrivals
                } else {
                    this.results = response.data.departures;
                }
            }
            this.calculateRemainingTimes();
            this.loading = false;
            this.startRemainingTimesCalcInterval();
        },
        calculateRemainingTimes() {
            const currentTime = Date.now();
            this.results.forEach(result => {
                if(!result.timestamp) return false;
                let resultTime = result.timestamp.realTime ? result.timestamp.realTime : result.timestamp.plannedTime;
                resultTime = new Date(resultTime);
                resultTime = resultTime.getTime();
                const timeDifference =(resultTime - currentTime) / 1000;
                if(timeDifference < 0) return false;
                result.remainingTime = formatDuration( timeDifference, true, true);
            }) 
        },
        startRemainingTimesCalcInterval() {
            this.calcTimeout = setInterval(() => {
                this.calculateRemainingTimes();
            }, 60 * 1000);
        },
        clearCalcTimeout() {
            if(!this.calcTimeout) return;
            clearInterval(this.calcTimeout);
        },
        setAvailableModes() {
            let availableModes = JSON.parse(JSON.stringify(PUBLIC_MEANS_OF_TRANSPORT_FULL));
            this.lines.forEach(line => {
                if(!line.type) return false;
                let modeIndex = availableModes.findIndex(mode => mode.value === line.type);
                if(modeIndex < 0 ) return false;
                if(availableModes[modeIndex].count) {
                    availableModes[modeIndex].count++;
                } else {
                    availableModes[modeIndex].count = 1;
                }
            })
            availableModes = availableModes.filter(mode => mode.count)
            if(availableModes.length <= 1) return false;
             // reset tabs
            this.tabs.forEach(tab => tab.available = false)
            for(const mode of availableModes) {
                const matchingTab = this.tabs.find(tab => tab.value.includes(mode.value))
                if(matchingTab) {
                    matchingTab.available = true
                    matchingTab.count += mode.count
                    continue;
                }
            }
            const availableTabs = this.tabs.filter(tab => tab.available === true)
            if(availableTabs.length <= 1) return false;
            availableTabs.sort((a, b) => (a.count < b.count) ? 1 : -1)
            this.availableModes = [
                {
                    value: 'all', 
                    label: 'Alle',
                    key: 'all',
                },
                ...availableTabs
            ];
        },
    }
}
</script>

<style lang="scss">
.station-monitor {
    &__sorting {
        position: relative;
        display: flex;
        align-items: center;
        margin-right: 24px;

        &__select {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;

            .el-input {  
                &__inner {
                    opacity: 0;
                    height: 100%;
                }
                &__suffix {
                    line-height: 1;
                    height: auto;
                    right: -2px;
                    transform: translateX(100%);
                    margin-top: -2px;
                }
                &__icon {
                    line-height: 1;
                    color: $color-grey-dark !important;
                }
            }
        }

        &__dropdown {
            @include sorting-dropdown;
            margin-left: -16px;
        }
    }
    &__modes {
        position: relative;
        &__slider {
            padding: 10px 0;
        }

        &__item {
            width: auto;
        }
        &__button {
            &.is-active {
                pointer-events: none;
            }
        }
    }
    &__times {
        margin-right: 16px;
        flex-shrink: 0;
    }
    &__infos {
        flex-grow: 1;
        &__icon {
            margin-right: 2px;
            flex-shrink: 0;
        }
        &__tag {
            margin-right: 6px;
            flex-shrink: 0;
        }
    }
    &__icons {
        flex-shrink: 0;
        &__item {
            color: $color-grey;
            margin-left: 8px;
        }
    }
    &__result {
        padding: 12px 0;
        border-bottom: $border-style;

        .tag {
            background: $color-grey-dark;
        }
    }

    &__cancellation {
        margin-top: 2px;
        &__title {
            gap: 2px;
            font-size: 15px;
            line-height: 1.2;

            &__icon {
                transition: 0.4s $ease-out-quart;
                .is-active & {
                    transform: rotate(180deg);
                }
            }
        }
        &__content {

        }
    }
}
</style>